import FcwMap from '@/components/molecules/FcwMap.vue';

import type FcwPointOfInterestSheet from '../../FcwPointOfInterestSheet.vue';
import type FcwStationSheet from '../../FcwStationSheet.vue';
import type FcwEntitiesMapLegend from '../../FcwEntitiesMapLegend/.FcwEntitiesMapLegend.vue';
import type { LngLatLike } from 'mapbox-gl';

interface UseSafeMapMarkerPositionOptions {
  stationSheetRef: Ref<InstanceType<typeof FcwStationSheet> | null>;
  pointOfInterestSheetRef: Ref<InstanceType<
    typeof FcwPointOfInterestSheet
  > | null>;
  legendRef: Ref<InstanceType<typeof FcwEntitiesMapLegend> | null>;
  safeMapAreaRef: Ref<HTMLElement | null>;
}

interface UseSafeMapMarkerReturn {
  refreshSafeArea: () => Promise<void>;
  easeToEntity: (entityCoordinates: Ref<LngLatLike | null>) => Promise<void>;
}

export function useSafeMapMarkerPosition(
  mapRef: Ref<InstanceType<typeof FcwMap> | null>,
  options: UseSafeMapMarkerPositionOptions
): UseSafeMapMarkerReturn {
  const {
    stationSheetRef,
    pointOfInterestSheetRef,
    legendRef,
    safeMapAreaRef,
  } = options;

  const safeAreaPadding = 48;
  const verticalOffset = 64;

  async function refreshSafeArea(): Promise<void> {
    const stationsRight =
      stationSheetRef.value?.$el?.getBoundingClientRect?.().right ?? 0;
    const pointsOfInterestRight =
      pointOfInterestSheetRef.value?.$el?.getBoundingClientRect?.().right ?? 0;

    const entityRight = Math.max(stationsRight, pointsOfInterestRight);
    if (safeMapAreaRef.value) {
      safeMapAreaRef.value.style.left = `${entityRight + safeAreaPadding}px`;
    }

    if (!safeMapAreaRef.value) return;

    const legendLeft =
      legendRef.value?.$el?.getBoundingClientRect?.().left ?? 0;
    if (safeMapAreaRef.value && legendLeft) {
      safeMapAreaRef.value.style.right = `${window.innerWidth - legendLeft + safeAreaPadding}px`;
    } else {
      safeMapAreaRef.value.style.right = `${safeAreaPadding}px`;
    }
  }

  const { isSmAndDown } = useFBreakpoints();
  /**
   * Ease to an entity on the map, if the entity is not in the safe area
   * @param entityCoordinates - Coordinates of the entity
   * @param screenCords - Screen coordinates of the entity
   */
  async function easeToEntity(
    entityCoordinates: Ref<LngLatLike | null>
  ): Promise<void> {
    if (!mapRef.value || !entityCoordinates.value || isSmAndDown.value) return;

    const { x, y } =
      (await mapRef.value.project(entityCoordinates.value as LngLatLike)) ?? {};
    if (!x || !y) return;

    await refreshSafeArea();

    if (!isPointInSafeArea(x, y) && entityCoordinates.value) {
      if (!safeMapAreaRef.value) return;
      const { top, left, bottom, right } =
        safeMapAreaRef.value.getBoundingClientRect();

      const padding = {
        top,
        left,
        bottom: window.innerHeight - bottom,
        right: window.innerWidth - right,
      };

      await mapRef.value?.easeTo(entityCoordinates.value as LngLatLike, {
        padding,
      });
    }
  }

  /**
   * Check if a point is in the safe map area
   * @param x - x coordinate
   * @param y - y coordinate
   */
  function isPointInSafeArea(x: number, y: number): boolean {
    if (!safeMapAreaRef.value) return false;

    const { top, left, bottom, right } =
      safeMapAreaRef.value.getBoundingClientRect();

    return (
      x >= left &&
      x <= right &&
      y >= top - verticalOffset &&
      y <= bottom - verticalOffset
    );
  }

  return { refreshSafeArea, easeToEntity };
}
